import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import {
    Avatar,
    Box,
    Container,
    Grid,
    Paper,
    Stack,
    Typography,
    useTheme
} from "@mui/material";
import { useContext } from "react";
import { isMobile } from "react-device-detect";
import { useTranslation } from "react-i18next";
import { Navigate } from "react-router-dom";
import { AppContext } from "src/contexts/AppContextProvider";
import { isMainSubdomain } from "src/GeneralHelper";
import CopyrightLeft from 'src/layouts/Footer/CopyrightLeft';
import OwnerRight from 'src/layouts/Footer/OwnerRight';
import Features from './components/UserReview/Features';
import LanguageSelector from './components/UserReview/LanguageSelector';
import StartButton from './components/UserReview/StartButton';
import UserReview from './components/UserReview/UserReview';
import './HomePage.css';

function HomePage() {
	const { t } = useTranslation();
	const ctx = useContext(AppContext);
	const theme = useTheme();

	if (!isMainSubdomain())
		return ctx.isLoggedIn ? (
			isMobile ? (
				<Navigate to="/scan" replace />
			) : (
				<Navigate to="/dashboard" replace />
			)
		) : (<Navigate to="/login" replace />);
	else
		return (
			<Box className="landing-page">
				{/* Header */}
				<Paper elevation={0} className="landing-header">
					<Container maxWidth="lg">
						<Box className="landing-header-container">
							<img src="header-logo.png" className="landing-logo" alt="Logo" />
							<LanguageSelector color="#1e293b" />
						</Box>
					</Container>
				</Paper>

				{/* Hero Section */}
				<Box className="hero-section">
					<Container maxWidth="lg">
						<Grid container spacing={6} alignItems="center">
							<Grid item xs={12} md={6}>
								<Stack spacing={4}>
									<Typography 
										variant="h1" 
										className="hero-title"
									>
										{t("HeroTitle")}
									</Typography>
									<Typography 
										variant="h6" 
										className="hero-subtitle"
									>
										{t("HeroBody")}
									</Typography>
									<Box display="flex" flexDirection="column" gap={2}>
										<StartButton />
										<Box display="flex" alignItems="center" gap={1}>
											<CheckCircleIcon className="hero-check-icon" />
											<Typography variant="body2" color="#64748b">
												{t("StartButtonSubtitle")}
											</Typography>
										</Box>
									</Box>
								</Stack>
							</Grid>
							<Grid item xs={12} md={6}>
								<Box 
									sx={{ 
										position: "relative",
										display: "flex",
										justifyContent: "center"
									}}
								>
									<Avatar 
										variant="rounded" 
										className="hero-image"
										alt="Product showcase" 
										src="landing.jpg" 
									/>
								</Box>
							</Grid>
						</Grid>
					</Container>
				</Box>

				{/* Social Proof */}
				<Box className="social-proof-section">
					<Container maxWidth="lg">
						<Typography 
							variant="h4" 
							align="center" 
							className="social-proof-title"
                            mb={1}
						>
							{t("TrustedByRetailers")}
						</Typography>
						<Grid container spacing={4} justifyContent="center">
							<Grid item xs={12} md={4}>
								<UserReview 
									company="Bordo Fashion" 
									name="Emre Uvut" 
									starRating={5} 
									review={t("Review1")} 
								/>
							</Grid>
							<Grid item xs={12} md={4}>
								<UserReview 
									company="Bordo Fashion" 
									name="Emre Uvut" 
									starRating={5} 
									review={t("Review2")} 
								/>
							</Grid>
							<Grid item xs={12} md={4}>
								<UserReview 
									company="Bordo Fashion" 
									name="Emre Uvut" 
									starRating={5} 
									review={t("Review3")} 
								/>
							</Grid>
						</Grid>
					</Container>
				</Box>

				{/* Features Section */}
				<Box className="features-section">
					<Container maxWidth="lg">
						<Stack spacing={2} alignItems="center" mb={8}>
							<Typography 
								variant="h3" 
								align="center" 
								className="features-title"
							>
								{t("FeaturesTitle")}
							</Typography>
							<Typography 
								variant="h6" 
								align="center" 
								className="features-subtitle"
							>
								{t("FeaturesSubtitle")}
							</Typography>
						</Stack>
						<Box className="features-grid">
							<Features />
						</Box>
					</Container>
				</Box>

				{/* Video Section */}
				<Box className="video-section">
					<Container maxWidth="md">
						<Stack spacing={4} alignItems="center">
							<Typography 
								variant="h3" 
								align="center" 
								className="video-title"
							>
								{t("VideoTitle")}
							</Typography>
							<Box className="video-container">
								<video 
									width="100%" 
									controls
									className="video-element"
								>
									<source src="intro.mp4" type="video/mp4" />
									{t("MissingVideoSupport")}
								</video>
							</Box>
						</Stack>
					</Container>
				</Box>

				{/* CTA Section */}
				<Box className="cta-section">
					<Container maxWidth="lg">
						<Stack spacing={4} alignItems="center" textAlign="center">
							<Typography 
								variant="h2" 
								className="cta-title"
							>
								{t("GiveItATry")}
							</Typography>
							<Typography 
								variant="h6" 
								className="cta-subtitle"
							>
								{t("CTASubtitle")}
							</Typography>
							<StartButton />
						</Stack>
					</Container>
				</Box>

				{/* Footer */}
				<Box className="footer-section">
					<Container maxWidth="lg">
						<Stack spacing={6}>
							<Box textAlign="center">
								<Typography 
									variant="body2" 
									className="footer-links"
								>
									{t("FooterLinks")}
								</Typography>
							</Box>
							<Box className="footer-content">
								<CopyrightLeft />
								<OwnerRight />
							</Box>
						</Stack>
					</Container>
				</Box>
			</Box>
		);
}

export default HomePage;
